import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Checkbox, Grid, IconButton, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'

import Button from '../components/Button'
import Dialog from '../components/Dialog'
import Select from '../components/Select'
import GridHeader from '../components/GridHeader'
import InputField from '../components/InputField'
import TimePicker from '../components/TimePicker'
import DatesPicker from '../components/DatesPicker'
import AutoComplete from '../components/AutoComplete'
import GridCheckbox from '../components/GridCheckbox'
import { useFeedback } from '../hooks/feedback-hook'
import { sortCodiceSocieta } from '../hooks/sort-functions'
import {
  useGetAffiliazioniLightQuery,
  useGetCampionatiQuery,
  useGetCategorieQuery,
  useGetImpostazioniAvanzateQuery,
  useGetImpostazioniContattiQuery,
  useGetIscrizioniQuery,
  useCreateIscrizioneMutation,
  useUpdateIscrizioneMutation,
} from '../hooks/api-slice'
import {
  fromDateToString,
  fromStringToDate,
  mailTo,
  formatIndirizzo,
  linkToGoogleMaps,
  formatBodyField,
  useSeasonRange,
} from '../hooks/utils-functions'
import { config } from '../settings/configs'
import {
  colours,
  titlesIscrizione as titles,
  phrasesIscrizione as phrases,
  GIORNI,
} from '../settings/settings'
import { setIscrizione, setIscrizioneField } from '../container/iscrizioni'

const AVANZ_FIELDS = [
  'OpzioniIscrizione',
  'OrariPartite',
  'LinkInfoPrivacy',
  'LinkRegolamento',
]

const Iscrizione = () => {
  const { admin, codiceUtente } = useSelector(state => state.home.infoUtente)
  const { data: avanzate } = useGetImpostazioniAvanzateQuery(AVANZ_FIELDS)
  const { data: contatti } = useGetImpostazioniContattiQuery()
  const { data: campionati } = useGetCampionatiQuery()

  const { data: affiliazioniLight = [] } = useGetAffiliazioniLightQuery(
    undefined,
    { skip: !admin },
  )
  const { data: categorie } = useGetCategorieQuery()
  const categorieAttive = categorie?.filter(c => c.isActive)

  const isMobile = useSelector(state => state.home.isMobile)
  const iscrizione = useSelector(state => state.iscrizioni.iscrizione)
  const { data: iscrizioni } = useGetIscrizioniQuery(
    {
      campionato: iscrizione?.codiceCampionato,
      codiceSocieta: iscrizione?.codiceSocieta,
    },
    { skip: !(iscrizione?.codiceCampionato && iscrizione?.codiceSocieta) },
  )

  const mode = !!iscrizione?._id ? 'update' : 'add'

  const [iscrCampionati, setIscrCampionati] = useState()
  const [iscrAperte, setIscrAperte] = useState()

  useEffect(() => {
    setIscrCampionati(
      campionati?.filter(
        c =>
          admin ||
          (mode === 'add' && !!c.iscrizioniAperte) ||
          (mode === 'update' && c.codice === iscrizione?.codiceCampionato),
      ),
    )
  }, [admin, campionati, iscrizione?.codiceCampionato, mode])

  useEffect(() => {
    setIscrAperte(
      !!campionati?.find(c => c.codice === iscrizione?.codiceCampionato)
        ?.iscrizioniAperte,
    )
  }, [campionati, iscrizione?.codiceCampionato])

  const [createIscrizioneMutation, { isLoading: isCreating }] =
    useCreateIscrizioneMutation()
  const [updateIscrizioneMutation, { isLoading: isUpdating }] =
    useUpdateIscrizioneMutation()

  const dispatch = useDispatch()

  const iDispatch = useCallback(
    (field, value, toCase) =>
      dispatch(
        setIscrizioneField({ field, value: formatBodyField(value, toCase) }),
      ),
    [dispatch],
  )

  const { OpzioniIscrizione, OrariPartite } = avanzate || {}
  const showSenzaPalestra = OpzioniIscrizione?.includes('senza-palestra')
  const showAltreRichieste = OpzioniIscrizione?.includes('altre-richieste')
  const showModuloPrivacy = OpzioniIscrizione?.includes('modulo-privacy')
  const showRegolamento = OpzioniIscrizione?.includes('regolamento')

  const docTitle = `${mode === 'add' ? 'Nuova' : 'Modifica'} iscrizione`
  document.title = `${config?.name} - ${docTitle}`

  // set "codiceSocieta"
  useEffect(() => {
    if (!admin && mode === 'add')
      if (!iscrizione?.codiceSocieta && codiceUtente)
        iDispatch('codiceSocieta', codiceUtente)
  }, [admin, codiceUtente, iDispatch, iscrizione?.codiceSocieta, mode])

  // update "motivoDeroga"
  useEffect(() => {
    if (!iscrizione?.hasDeroga && iscrizione?.motivoDeroga)
      iDispatch('motivoDeroga')
  }, [iDispatch, iscrizione?.hasDeroga, iscrizione?.motivoDeroga])

  const isNotEditable = !admin && iscrizione?.codiceCampionato && !iscrAperte

  const { setFeedback } = useFeedback()

  const [openInfoNome, setOpenInfoNome] = useState()
  const [openInfoNote, setOpenInfoNote] = useState()
  const [openInfoPreferenze, setOpenInfoPreferenze] = useState()
  const [openIndisp, setOpenIndisp] = useState()
  const [openIndispTyping, setOpenIndispTyping] = useState()
  const [openDeroga, setOpenDeroga] = useState()

  const handleClickOpenInfoNome = () => setOpenInfoNome(true)
  const handleClickOpenInfoNote = () => setOpenInfoNote(true)
  const handleClickOpenInfoPreferenze = () => setOpenInfoPreferenze(true)
  const handleClickOpenIndisp = () => setOpenIndisp(true)
  const handleClickOpenIndispTyping = () => setOpenIndispTyping(true)
  const handleClickOpenDeroga = () => setOpenDeroga(true)

  const handleCloseInfoNome = () => setOpenInfoNome()
  const handleCloseInfoNote = () => setOpenInfoNote()
  const handleCloseInfoPreferenze = () => setOpenInfoPreferenze()
  const handleCloseIndisp = () => setOpenIndisp()
  const handleCloseIndispTyping = () => setOpenIndispTyping()
  const handleCloseDeroga = () => setOpenDeroga()

  // update "senzaPalestra" variables
  useEffect(() => {
    if (iscrizione?.senzaPalestra) {
      if (iscrizione?.giorno) iDispatch('giorno')
      if (iscrizione?.orario) iDispatch('orario')
      if (iscrizione?.presso) iDispatch('presso')
      if (iscrizione?.indirizzo) iDispatch('indirizzo')
      if (iscrizione?.citta) iDispatch('citta')
      if (iscrizione?.abbinamenti) iDispatch('abbinamenti')
      if (iscrizione?.alternanze) iDispatch('alternanze')
      if (iscrizione?.indisponibilitaPalestra)
        iDispatch('indisponibilitaPalestra')
    }
  }, [
    iDispatch,
    iscrizione?.abbinamenti,
    iscrizione?.alternanze,
    iscrizione?.citta,
    iscrizione?.giorno,
    iscrizione?.indirizzo,
    iscrizione?.indisponibilitaPalestra,
    iscrizione?.orario,
    iscrizione?.presso,
    iscrizione?.senzaPalestra,
  ])

  // parameters for indisponibilità
  const { minDate, maxDate } = useSeasonRange()

  // format indisponibilitaPalestra
  const fIndPal = () =>
    iscrizione?.indisponibilitaPalestra?.map(i => fromStringToDate(i)) || []

  const setIndisponibilitaPalestra = i =>
    iDispatch(
      'indisponibilitaPalestra',
      i?.sort((a, b) => a - b)?.map(x => fromDateToString(new Date(x))) || [],
    )

  const addIscrizione = async () => {
    const result = await createIscrizioneMutation(iscrizione)
    if (!result?.error)
      setFeedback(titles.titleCompleteAdd, false, '', '/iscrizioni')
  }

  const updateIscrizione = async () => {
    const arg = { id: iscrizione?._id, ...iscrizione }
    const result = await updateIscrizioneMutation(arg)
    if (!result?.error)
      setFeedback(titles.titleCompleteUpdate, false, '', '/iscrizioni')
  }

  const confirmIscrizione = async () => {
    if (mode === 'add') await addIscrizione()
    if (mode === 'update') await updateIscrizione()
  }

  // trigger functions onComponentUnmounts
  useEffect(() => {
    return () => dispatch(setIscrizione())
  }, [dispatch])

  const containerProps = {
    container: true,
    paddingY: 2,
    rowSpacing: 2,
    columnSpacing: 5,
    alignItems: 'center',
    sx: { width: '100%' },
  }

  return (
    <Grid container paddingX={2} sx={{ width: '100%' }}>
      <GridHeader h2 text={docTitle} />
      {!admin && (
        <Grid size={12}>
          <center>
            {mode === 'add' && !iscrCampionati?.length && (
              <h4 style={{ color: colours.red }}>
                Le iscrizioni ai campionati sono chiuse.
              </h4>
            )}
            {mode === 'update' && !iscrAperte && (
              <h4 style={{ color: colours.red }}>
                Le iscrizioni a questo campionato sono chiuse. È possibile solo
                modificare i contatti dei referenti.
              </h4>
            )}
            <p>
              Per domande o richieste scrivere a{' '}
              {mailTo(contatti?.MailIscrizioni)}.
            </p>
          </center>
        </Grid>
      )}
      <Grid {...containerProps}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            id='codice-campionato'
            label='Campionato *'
            value={iscrizione?.codiceCampionato}
            options={iscrCampionati?.map(c => c.codice)}
            optionLabels={iscrCampionati?.map(c => c.nome)}
            disabled={!iscrCampionati}
            onChange={i => iDispatch('codiceCampionato', i)}
            errorFunc={() => !iscrizione?.codiceCampionato}
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
        {admin && (
          <Grid size={{ xs: 12, md: 6 }}>
            <AutoComplete
              id='codice-societa'
              value={affiliazioniLight?.find(
                s => s.codiceSocieta === Number(iscrizione?.codiceSocieta),
              )}
              options={[...affiliazioniLight]?.sort((a, b) =>
                sortCodiceSocieta(a.codiceSocieta, b.codiceSocieta),
              )}
              getOptionLabel={op => `${op.codiceSocieta} - ${op.nomeSocieta}`}
              label='Società *'
              disabled={!affiliazioniLight}
              onChange={i => iDispatch('codiceSocieta', i?.codiceSocieta)}
              errorFunc={() => !iscrizione?.codiceSocieta}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            id='categoria'
            value={iscrizione?.categoria}
            options={categorieAttive?.map(c => c.categoria)}
            optionLabels={categorieAttive?.map(c => c.nome)}
            label='Categoria *'
            onChange={i => iDispatch('categoria', i)}
            errorFunc={() => !iscrizione?.categoria}
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='nome-squadra'
            value={iscrizione?.nomeSquadra}
            onChange={i => iDispatch('nomeSquadra', i, 'upper')}
            onBlur={admin ? () => {} : handleClickOpenInfoNome}
            autoFocus={!admin && iscrAperte && mode === 'add'}
            label='Nome della squadra *'
            errorFunc={() => !iscrizione?.nomeSquadra}
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter text='Gare casalinghe' />
        {showSenzaPalestra && (
          <GridCheckbox
            label='Palestra non disponibile'
            value={!!iscrizione?.senzaPalestra}
            disabled={isNotEditable}
            onChange={(_, x) => iDispatch('senzaPalestra', x)}
            md={12}
            justifyContent='flex-start'
          />
        )}
        {iscrizione?.senzaPalestra && !showSenzaPalestra && (
          <Grid size={12} paddingY={2} style={{ color: colours.red }}>
            La squadra è senza palestra.
          </Grid>
        )}
        {!iscrizione?.senzaPalestra && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                id='giorno'
                value={iscrizione?.giorno}
                options={OrariPartite?.map(x => x.giorno)}
                label='Giorno di disputa'
                onChange={i => {
                  iDispatch('giorno', i)
                  iDispatch('indisponibilitaPalestra')
                }}
                errorFunc={() => !iscrizione?.giorno}
                mode={mode}
                readOnly={isNotEditable}
                showEmpty
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <center>
                <TimePicker
                  label='Orario di disputa'
                  value={iscrizione?.orario}
                  onChange={i => iDispatch('orario', i)}
                  error={!iscrizione?.orario}
                  readOnly={isNotEditable}
                  minTime={
                    OrariPartite?.find(i => i.giorno === iscrizione?.giorno)
                      ?.dalle
                  }
                  maxTime={
                    OrariPartite?.find(i => i.giorno === iscrizione?.giorno)
                      ?.alle
                  }
                  mode={mode}
                />
              </center>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <InputField
                id='presso'
                value={iscrizione?.presso}
                onChange={i => iDispatch('presso', i, 'upper')}
                label='Istituto o centro sportivo'
                adornment='place'
                mode={mode}
                readOnly={isNotEditable}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <InputField
                id='indirizzo'
                value={iscrizione?.indirizzo}
                onChange={i => iDispatch('indirizzo', i, 'upper')}
                label='Indirizzo'
                adornment='zone'
                errorFunc={() => !iscrizione?.indirizzo}
                mode={mode}
                readOnly={isNotEditable}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <InputField
                id='città'
                value={iscrizione?.citta}
                onChange={i => iDispatch('citta', i, 'upper')}
                label='Città'
                adornment='city'
                errorFunc={() => !iscrizione?.citta}
                mode={mode}
                readOnly={isNotEditable}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {linkToGoogleMaps(formatIndirizzo(iscrizione))}
            </Grid>
          </>
        )}
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter text='Primo referente della squadra' />
        <Grid size={{ xs: 12, md: 4 }}>
          <InputField
            id='nome-referente'
            value={iscrizione?.nomeReferente}
            onChange={i => iDispatch('nomeReferente', i, 'upper')}
            label='Nome e Cognome'
            adornment='person'
            errorFunc={() => !iscrizione?.nomeReferente}
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InputField
            id='telefono-referente'
            value={iscrizione?.telefonoReferente}
            onChange={i =>
              iDispatch('telefonoReferente', i?.replace(/\s/g, ''))
            }
            label='Telefono'
            type='tel'
            adornment='phone'
            errorFunc={() => !iscrizione?.telefonoReferente}
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InputField
            id='mail-referente'
            value={iscrizione?.mailReferente}
            onChange={i =>
              iDispatch('mailReferente', i?.replace(/\s/g, ''), 'lower')
            }
            label='E-mail'
            type='email'
            adornment='email'
            errorFunc={() => !iscrizione?.mailReferente}
            mode={mode}
          />
        </Grid>
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter text='Secondo referente della squadra' />
        <Grid size={{ xs: 12, md: 4 }}>
          <InputField
            id='nome-referente-2'
            value={iscrizione?.nomeReferente2}
            onChange={i => iDispatch('nomeReferente2', i, 'upper')}
            label='Nome e Cognome'
            adornment='person'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InputField
            id='telefono-referente-2'
            value={iscrizione?.telefonoReferente2}
            onChange={i =>
              iDispatch('telefonoReferente2', i?.replace(/\s/g, ''))
            }
            label='Telefono'
            type='tel'
            adornment='phone'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InputField
            id='mail-referente-2'
            value={iscrizione?.mailReferente2}
            onChange={i =>
              iDispatch('mailReferente2', i?.replace(/\s/g, ''), 'lower')
            }
            label='E-mail'
            type='email'
            adornment='email'
            mode={mode}
          />
        </Grid>
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter>
          Preferenze di calendario &nbsp;
          <IconButton
            style={{ color: colours.grey }}
            onClick={handleClickOpenInfoPreferenze}
          >
            <Tooltip title='Maggiori informazioni'>
              <InfoIcon />
            </Tooltip>
          </IconButton>
        </GridHeader>
        <Grid size={{ xs: 12, md: 6 }}>
          <AutoComplete
            id='abbinamenti'
            value={iscrizioni?.filter(s =>
              iscrizione?.abbinamenti?.includes(s.id),
            )}
            blurOnSelect={false}
            multiple
            options={iscrizioni?.filter(
              s =>
                s.id !== iscrizione?._id &&
                !iscrizione?.alternanze?.includes(s.id) &&
                !s.senzaPalestra &&
                s.codiceSocieta ===
                  Number((admin && iscrizione?.codiceSocieta) || codiceUtente),
            )}
            getOptionLabel={op => `${op.categoria} ${op.nomeSquadra}`}
            noOptionsText='Nessun abbinamento possibile'
            label='Abbinamenti'
            onChange={i =>
              iDispatch(
                'abbinamenti',
                i.map(a => a.id),
              )
            }
            mode={mode}
            disabled={iscrizione?.senzaPalestra}
            readOnly={isNotEditable}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <AutoComplete
            id='alternanze'
            value={iscrizioni?.filter(s =>
              iscrizione?.alternanze?.includes(s.id),
            )}
            blurOnSelect={false}
            multiple
            options={iscrizioni?.filter(
              s =>
                s.id !== iscrizione?._id &&
                !iscrizione?.abbinamenti?.includes(s.id) &&
                !s.senzaPalestra &&
                s.codiceSocieta ===
                  Number((admin && iscrizione?.codiceSocieta) || codiceUtente),
            )}
            getOptionLabel={op => `${op.categoria} ${op.nomeSquadra}`}
            noOptionsText='Nessuna alternanza possibile'
            label='Alternanze'
            onChange={i =>
              iDispatch(
                'alternanze',
                i.map(a => a.id),
              )
            }
            mode={mode}
            disabled={iscrizione?.senzaPalestra}
            readOnly={isNotEditable}
          />
        </Grid>
        {iscrizione?.senzaPalestra && (
          <Grid size={12} paddingY={2} style={{ color: colours.red }}>
            Le squadre senza palestra non possono inserire preferenze di
            abbinamento o alternanza.
          </Grid>
        )}
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter>
          Indisponibilità palestra &nbsp;
          <IconButton
            style={{ color: colours.grey }}
            onClick={handleClickOpenIndisp}
          >
            <Tooltip title='Maggiori informazioni'>
              <InfoIcon />
            </Tooltip>
          </IconButton>
        </GridHeader>
        <DatesPicker
          id='indisponibilita-palestra'
          label='Indisponibilità palestra'
          value={fIndPal()}
          onChange={setIndisponibilitaPalestra}
          onInputChange={handleClickOpenIndispTyping}
          minDate={minDate}
          maxDate={maxDate}
          onlyDays={[(GIORNI.indexOf(iscrizione?.giorno) + 1) % 7]}
          mode={mode}
          isMobile={isMobile}
          disabled={iscrizione?.senzaPalestra || !iscrizione?.giorno}
          readOnly={isNotEditable}
        />
        {(iscrizione?.senzaPalestra || !iscrizione?.giorno) && (
          <Grid size={12} paddingY={2} style={{ color: colours.red }}>
            Le squadre senza palestra o senza giorno di disputa non possono
            inserire indisponibilità della palestra.
          </Grid>
        )}
      </Grid>
      &nbsp;
      {showAltreRichieste && (
        <Grid {...containerProps}>
          <Grid size={12}>
            <InputField
              id='note'
              value={iscrizione?.note}
              onChange={i => iDispatch('note', i)}
              onBlur={admin ? () => {} : handleClickOpenInfoNote}
              multiline
              label='Altre richieste particolari'
              mode={mode}
              readOnly={isNotEditable}
            />
          </Grid>
        </Grid>
      )}
      {showModuloPrivacy && (!admin || mode === 'add') && (
        <Grid {...containerProps}>
          <GridHeader
            noCenter
            text={`Legge sulla privacy e accettazione Statuto e Regolamenti di ${config?.name}`}
          />
          <Grid size={{ xs: 2, md: 1 }}>
            <center>
              <Checkbox
                id='check-privacy'
                checked={!!iscrizione?.checkPrivacy}
                disabled={isNotEditable}
                onChange={event =>
                  iDispatch('checkPrivacy', event.target.checked)
                }
                color='default'
              />
            </center>
          </Grid>
          <Grid size={{ xs: 10, md: 11 }}>
            <div style={{ color: colours.greyDark }}>
              Dichiaro di aver preso visione dell'
              <a
                target='_blank'
                rel='noreferrer noopener'
                href={avanzate?.LinkInfoPrivacy}
              >
                informativa
              </a>{' '}
              sul trattamento dei dati personali e di prestare il consenso al
              loro trattamento per gli scopi indicati. La società dichiara
              inoltre di accettare lo Statuto e i Regolamenti di {config?.name}{' '}
              e che i propri atleti tesserati sono in possesso dell'idonea
              certificazione medica per l'attività sportiva.
            </div>
          </Grid>
        </Grid>
      )}
      {showRegolamento && (!admin || mode === 'add') && (
        <Grid {...containerProps}>
          <GridHeader
            noCenter
            text={`Presa visione del Regolamento di ${config?.name}`}
          />
          <Grid size={{ xs: 2, md: 1 }}>
            <center>
              <Checkbox
                id='check-regolamento'
                checked={!!iscrizione?.checkRegolamento}
                disabled={isNotEditable}
                onChange={event =>
                  iDispatch('checkRegolamento', event.target.checked)
                }
                color='default'
              />
            </center>
          </Grid>
          <Grid size={{ xs: 10, md: 11 }}>
            <div style={{ color: colours.greyDark }}>
              Dichiaro di aver preso visione del{' '}
              <a
                target='_blank'
                rel='noreferrer noopener'
                href={avanzate?.LinkRegolamento}
              >
                regolamento
              </a>{' '}
              di {config?.name}.
            </div>
          </Grid>
        </Grid>
      )}
      {admin && (
        <Grid {...containerProps} alignItems='center'>
          <GridHeader noCenter>
            Deroga e fuori classifica &nbsp;
            <IconButton
              style={{ color: colours.grey }}
              onClick={handleClickOpenDeroga}
            >
              <Tooltip title='Maggiori informazioni'>
                <InfoIcon />
              </Tooltip>
            </IconButton>
          </GridHeader>
          <GridCheckbox
            label='Deroga concessa'
            value={!!iscrizione?.hasDeroga}
            onChange={(_, x) => iDispatch('hasDeroga', x)}
            md={2}
            justifyContent='flex-start'
          />
          {iscrizione?.hasDeroga && (
            <Grid size={{ xs: 12, md: 4 }}>
              <InputField
                id='motivo-deroga'
                value={iscrizione?.motivoDeroga}
                onChange={i => iDispatch('motivoDeroga', i)}
                label='Motivo deroga'
                mode={mode}
              />
            </Grid>
          )}
          <GridCheckbox
            label='Fuori classifica'
            value={!!iscrizione?.isFuoriClassifica}
            onChange={(_, x) => iDispatch('isFuoriClassifica', x)}
            md={2}
            justifyContent='flex-start'
          />
          {iscrizione?.isFuoriClassifica && (
            <Grid size={{ xs: 12, md: 4 }}>
              <InputField
                id='motivo-fuori-classifica'
                value={iscrizione?.motivoFuoriClassifica}
                onChange={i => iDispatch('motivoFuoriClassifica', i)}
                label='Motivo fuori classifica'
                mode={mode}
              />
            </Grid>
          )}
        </Grid>
      )}
      <Grid {...containerProps}>
        <Grid size='grow'>
          <center>
            <Button error to='/iscrizioni' children='Indietro' />
          </center>
        </Grid>
        <Grid size='grow'>
          <center>
            <Button
              dark
              loading={isCreating || isUpdating}
              onClick={confirmIscrizione}
            >
              Salva
            </Button>
          </center>
        </Grid>
      </Grid>
      <Dialog
        open={openInfoNome}
        handleClose={handleCloseInfoNome}
        title={titles.titleInfoNome}
        textContent={phrases.phraseInfoNome}
      />
      <Dialog
        open={openInfoNote}
        handleClose={handleCloseInfoNote}
        title={titles.titleInfoNote}
        textContent={phrases.phraseInfoNote}
      />
      <Dialog
        open={openInfoPreferenze}
        handleClose={handleCloseInfoPreferenze}
        title={titles.titleInfoPreferenze}
        textContent={phrases.phraseInfoPreferenze}
        maxWidth='md'
      />
      <Dialog
        open={openIndisp}
        handleClose={handleCloseIndisp}
        title={titles.titleIndisp}
        textContent={phrases.phraseIndisp}
      />
      <Dialog
        open={openIndispTyping}
        handleClose={handleCloseIndispTyping}
        title={titles.titleIndispTyping}
        textContent={phrases.phraseIndispTyping}
      />
      <Dialog
        open={openDeroga}
        handleClose={handleCloseDeroga}
        title={titles.titleDeroga}
        textContent={phrases.phraseDeroga}
      />
    </Grid>
  )
}

export default Iscrizione
